import type { SortStatus } from '@dixa/lib-shared-habla';
import {
  Box,
  Column,
  Columns,
  DataTable,
  FlexBox,
  HelpIcon,
  IconButton,
  InputText,
  Skeleton,
  Text,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import type { QuickViewsSortKey } from 'lib-analytics-types';
import { AnalyticsMenu, AnalyticsMenuActions } from 'lib-analytics-ui';
import { useHasAccess } from 'lib-shared-access-control';
import type { Sort, SortDirection } from 'lib-shared-types-api';
import { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';

import { analyticsApi } from 'apis/analytics';
import { AppLink } from 'components/appLink';
import { UserProfile } from 'components/userProfile/userProfile';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { articles } from 'utils/elevio';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { AnalyticsDocumentationButton } from '../../components/documentation/analyticsDocumentationButton';
import { AnalyticsHeader } from '../../components/header/analyticsHeader';
import { AnalyticsQuickViewFavoriteToggle } from '../../components/quickView/components/favorite/quickViewFavoriteToggle';
import { useQuickViewActions } from '../quickView/components/quickView/utils/useQuickViewActions/useQuickViewActions';

export const QuickViewsView = (): JSX.Element => {
  const hasAnalyticsAccess = useHasAccess('analytics');
  const { user } = useCurrentUser();
  const { meta } = useAppSelector((state) => state.analytics);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [sortStatus, setSortStatus] = useState<SortStatus | null>({
    sortBy: 'favorited',
    direction: 'desc',
  });

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const {
    handleUpdateQuickView,
    handleDuplicateQuickView,
    handleDeleteQuickView,
    handleUpdateQuickViewFavoriteState,
    handleTransferQuickViewOwnership,
  } = useQuickViewActions();

  const {
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items: quickViews,
    changeArg,
    nextPage,
    previousPage,
    refetchItems,
    updateItem,
    search,
    arg,
  } = usePaginatedEndpoint({
    endpoint: analyticsApi.endpoints.readQuickViews,
    itemsSelector: ({ data: { quick_views } }) => quick_views,
    initialArg: {
      limit: 50,
      offset: 0,
      sort: [
        { key: 'favorited', direction: 'desc' },
        { key: 'name', direction: 'asc' },
      ],
    },
  });

  const isSuccess = asyncStatus === 'fulfilled';
  const isError = asyncStatus === 'rejected';
  const isLoading = asyncStatus === 'pending';

  /* Handles sorting of quick views. */
  const handleChangeSort = (status: SortStatus | null): void => {
    setSortStatus(status);

    if (status) {
      const sort: Sort<QuickViewsSortKey> = {
        key: status.sortBy as QuickViewsSortKey,
        direction: status.direction as SortDirection,
      };

      // If we're sorting by name, we leave it as is, otherwise we sort by the requested sort key
      // and then by name in ascending order.
      changeArg({
        sort:
          sort.key === 'name'
            ? sort
            : [sort, { key: 'name', direction: 'asc' }],
      });
    } else {
      changeArg({
        sort: {
          key: 'name',
          direction: 'asc',
        },
      });
    }
  };

  /* Handles search query change. */
  const handleChangeQuery = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
  };

  /* Handles search request. */
  const handleSubmitQuery = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      search(query, { debounceTime: 300 });
    }
  };

  /**
   * Handles clearing the search query. If the query is submitted, clear it and
   * refetch data from the API. Otherwise just clear the query.
   */
  const handleClearQuery = (): void => {
    setQuery('');

    if (query === arg?.searchTerm) {
      search('');
    }

    searchInputRef.current?.focus();
  };

  return (
    <Columns>
      <Column>
        <Column.Row slottable borderBottom>
          <Column.Slot>
            <AnalyticsHeader tier={meta.tier}>
              <Text.Heading color="primary" as="h2">
                All saved dashboards
              </Text.Heading>

              <AnalyticsDocumentationButton
                type="article"
                articleId={articles.analytics.quickViews}
              />
            </AnalyticsHeader>
          </Column.Slot>

          <Column.Slot noVerticalPadding placement="end">
            <Box width={isSmallScreen ? undefined : '300px'}>
              <InputText
                ref={searchInputRef}
                name="quick_views_search"
                type="text"
                value={query}
                placeholder="Search by name..."
                onChange={handleChangeQuery}
                onKeyDown={handleSubmitQuery}
                suffix={
                  !isSmallScreen
                    ? {
                        type: 'custom',
                        value: (
                          <FlexBox alignItems="center" padding="0 100">
                            <IconButton
                              variant="tertiary"
                              icon={query ? 'close' : 'search'}
                              onClick={query ? handleClearQuery : undefined}
                            />
                          </FlexBox>
                        ),
                        noWrap: true,
                      }
                    : undefined
                }
              />
            </Box>
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1}>
          <DataTable
            sortingProps={{
              sortStatus,
              onSortChange: handleChangeSort,
            }}
            paginationProps={{
              previousButtonDisabled: !hasPreviousPage || !isSuccess,
              previousButtonClick: previousPage,
              nextButtonDisabled: !hasNextPage || !isSuccess,
              nextButtonClick: nextPage,
            }}
            tableStatusProps={{
              loadingFailed: isError,
              onLoadingRetry: refetchItems,
              emptyState:
                !quickViews.length && isSuccess
                  ? {
                      title: 'Get started with saved dashboards',
                      description:
                        'Navigate to any existing dashboard, customize the period or filters and click the "Pin to dashboard" action in the 3-dot menu of any widget to save your first custom dashboard.',
                      buttonProps: {
                        variant: 'primary',
                        children: 'Learn more',
                        onClick: () => {
                          elevioClient.openArticle(
                            articles.analytics.quickViews,
                          );
                        },
                      },
                    }
                  : undefined,
            }}
            columns={[
              {
                label: '',
                columnId: 'favorited',
                sortable: true,
                selector: (quickView) => {
                  if (isLoading) {
                    return (
                      <FlexBox
                        justifyContent="center"
                        alignItems="center"
                        width="32px"
                        height="32px"
                      >
                        <Skeleton height="18px" width="18px" />
                      </FlexBox>
                    );
                  }

                  return (
                    <Tooltip
                      content={
                        quickView.favorited
                          ? 'Remove from the sidebar'
                          : 'Add to the sidebar'
                      }
                      side="top"
                    >
                      <AnalyticsQuickViewFavoriteToggle
                        favorited={quickView.favorited}
                        onClick={async () => {
                          await handleUpdateQuickViewFavoriteState(quickView);

                          // Optimistically updates the quick view's favorite status in the table.
                          updateItem(
                            { ...quickView, favorited: !quickView.favorited },
                            (curr, next) => curr.id === next.id,
                          );
                        }}
                      />
                    </Tooltip>
                  );
                },
                style: {
                  width: 0,
                },
              },
              {
                label: 'Name',
                columnId: 'name',
                sortable: true,
                selector: (quickView) => {
                  if (isLoading) {
                    return <Skeleton height="14px" width="80%" />;
                  }

                  return (
                    <FlexBox gap="200" alignItems="center">
                      <AppLink
                        path="/analytics/dashboard/custom/:id"
                        params={{
                          path: {
                            id: quickView.id,
                          },
                        }}
                      >
                        {quickView.name}
                      </AppLink>

                      {quickView.description && (
                        <HelpIcon text={quickView.description} />
                      )}
                    </FlexBox>
                  );
                },
              },
              {
                label: 'Privacy setting',
                columnId: 'shared',
                sortable: true,
                selector: (quickView) => {
                  if (isLoading) {
                    return <Skeleton height="14px" width="40%" />;
                  }

                  return quickView.shared ? 'Public' : 'Private';
                },
              },
              {
                label: 'Updated',
                columnId: 'updated_at',
                sortable: true,
                selector: (quickView) => {
                  if (isLoading) {
                    return <Skeleton height="14px" width="50%" />;
                  }

                  return quickView.updated_at ? (
                    <Timestamp time={quickView.updated_at} relative />
                  ) : (
                    '-'
                  );
                },
              },
              {
                label: 'Owner',
                selector: (quickView) => {
                  if (isLoading) {
                    return (
                      <FlexBox gap="16px" alignItems="center">
                        <Skeleton
                          width="24px"
                          height="24px"
                          borderRadius="100"
                        />
                        <Skeleton height="14px" width="70%" />
                      </FlexBox>
                    );
                  }

                  return <UserProfile user={quickView.user} />;
                },
              },
              {
                label: ' ',
                selector: (quickView) => {
                  if (isLoading) {
                    return (
                      <FlexBox
                        justifyContent="center"
                        alignItems="center"
                        width="32px"
                        height="32px"
                      >
                        <Skeleton height="18px" width="18px" />
                      </FlexBox>
                    );
                  }

                  /**
                   * Only the owner of the quick view can modify its details and
                   * visibility.
                   */
                  const isOwner = quickView?.user.id === user.id;

                  /**
                   * Only the owner and users with full access to Analytics can
                   * transfer ownership of quick views that are shared.
                   */
                  const canTransferOwnership =
                    (isOwner || hasAnalyticsAccess) && !!quickView?.shared;

                  return (
                    <AnalyticsMenu
                      actions={[
                        ...(isOwner
                          ? ([
                              {
                                type: 'button',
                                icon: 'pen',
                                label: 'Edit',
                                onClick: async () => {
                                  const result = await handleUpdateQuickView(
                                    quickView,
                                  );

                                  updateItem(
                                    result.quickView,
                                    (curr, next) => curr.id === next.id,
                                  );
                                },
                              },
                            ] as AnalyticsMenuActions)
                          : []),
                        {
                          type: 'button',
                          icon: 'copy',
                          label: 'Duplicate',
                          onClick: () => handleDuplicateQuickView(quickView),
                        },
                        ...(canTransferOwnership
                          ? ([
                              {
                                type: 'divider',
                              },
                              {
                                type: 'button',
                                icon: 'exchange',
                                label: 'Transfer ownership',
                                onClick: async () => {
                                  const result =
                                    await handleTransferQuickViewOwnership(
                                      quickView,
                                    );

                                  updateItem(
                                    result.quickView,
                                    (curr, next) => curr.id === next.id,
                                  );
                                },
                              },
                            ] as AnalyticsMenuActions)
                          : []),
                        ...(isOwner
                          ? ([
                              {
                                type: 'divider',
                              },
                              {
                                type: 'button',
                                icon: 'trash',
                                label: 'Delete',
                                onClick: async () => {
                                  const { isDone } =
                                    await handleDeleteQuickView(quickView);
                                  isDone && refetchItems();
                                },
                              },
                            ] as AnalyticsMenuActions)
                          : []),
                      ]}
                    />
                  );
                },
                style: {
                  width: 0,
                },
              },
            ]}
            data={
              isLoading
                ? (Array.from({ length: 20 }) as typeof quickViews)
                : quickViews
            }
          />
        </Column.Row>
      </Column>
    </Columns>
  );
};
